import { useState, useEffect } from 'react';
import {
  Activity,
  Database,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Users,
  Building2,
  FileText,
  BarChart3,
  RefreshCw,
  AlertCircle,
  Target,
  Pill,
  Zap,
  DollarSign,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import api from '../../services/api';

// Coverage progress bar
function CoverageBar({ label, count, total, pct, colorClass = 'bg-blue-500' }) {
  const getColor = () => {
    if (pct >= 80) return 'bg-green-500';
    if (pct >= 50) return 'bg-yellow-500';
    if (pct >= 20) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-32 text-sm text-gray-600">{label}</div>
      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${getColor()} transition-all duration-300`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="w-20 text-right">
        <span className={`text-sm font-semibold ${pct < 50 ? 'text-red-600' : 'text-gray-700'}`}>
          {pct}%
        </span>
        <span className="text-xs text-gray-400 ml-1">({count})</span>
      </div>
    </div>
  );
}

// Stat card
function StatCard({ icon: Icon, label, value, subValue, colorClass = 'text-blue-600', bgClass = 'bg-blue-50' }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 ${bgClass} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${colorClass}`} />
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{label}</div>
          {subValue && <div className="text-xs text-gray-400">{subValue}</div>}
        </div>
      </div>
    </div>
  );
}

// Issue card
function IssueCard({ issue }) {
  const getIcon = () => {
    if (issue.severity === 'critical') return <AlertCircle className="w-5 h-5 text-red-500" />;
    if (issue.severity === 'warning') return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    return <AlertCircle className="w-5 h-5 text-blue-500" />;
  };

  const getBg = () => {
    if (issue.severity === 'critical') return 'bg-red-50 border-red-200';
    if (issue.severity === 'warning') return 'bg-yellow-50 border-yellow-200';
    return 'bg-blue-50 border-blue-200';
  };

  return (
    <div className={`p-3 rounded-lg border ${getBg()} flex items-start gap-3`}>
      {getIcon()}
      <div>
        <div className="text-sm font-medium text-gray-900">{issue.message}</div>
        <div className="text-xs text-gray-500 mt-0.5">{issue.count} records affected</div>
      </div>
    </div>
  );
}

// Distribution bar
function DistributionBar({ items, maxCount }) {
  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <div className="w-28 text-xs text-gray-600 truncate" title={item.name}>
            {item.name?.replace(/_/g, ' ')}
          </div>
          <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-500"
              style={{ width: `${maxCount > 0 ? (item.count / maxCount) * 100 : 0}%` }}
            />
          </div>
          <div className="w-8 text-xs text-gray-500 text-right">{item.count}</div>
        </div>
      ))}
    </div>
  );
}

// Collapsible section
function Section({ title, icon: Icon, iconColor = 'text-gray-500', children, defaultOpen = true, badge }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <h3 className="font-semibold text-gray-900">{title}</h3>
          {badge && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {isOpen && <div className="px-4 pb-4 border-t border-gray-100">{children}</div>}
    </div>
  );
}

export default function AdminDataHealth() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/data-health');
      setData(response.data?.data);
    } catch (err) {
      console.error('Failed to load data health:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading data health metrics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={loadData} className="px-4 py-2 bg-indigo-600 text-white rounded-lg">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const signals = data?.signals || {};
  const deals = data?.deals || {};
  const platform = data?.platform || {};
  const distributions = data?.distributions || {};
  const dataIssues = data?.dataIssues || [];

  // Calculate overall health score
  const signalTACoverage = signals.coverage?.therapeutic_area?.pct || 0;
  const signalModalityCoverage = signals.coverage?.modality?.pct || 0;
  const dealTACoverage = deals.coverage?.therapeutic_area?.pct || 0;
  const dealModalityCoverage = deals.coverage?.modality?.pct || 0;
  const overallHealth = Math.round((signalTACoverage + signalModalityCoverage + dealTACoverage + dealModalityCoverage) / 4);

  const getHealthColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const maxSignalTypeCount = Math.max(...(distributions.signalTypes?.map(s => s.count) || [1]));
  const maxTACount = Math.max(...(distributions.therapeuticAreas?.map(t => t.count) || [1]));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Database className="w-6 h-6 text-indigo-600" />
            Data Health Dashboard
          </h1>
          <p className="text-gray-600">Monitor platform data quality and coverage</p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Data Issues Alert */}
      {dataIssues.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5" />
            Data Quality Issues ({dataIssues.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {dataIssues.map((issue, idx) => (
              <IssueCard key={idx} issue={issue} />
            ))}
          </div>
        </div>
      )}

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Activity}
          label="Overall Health"
          value={`${overallHealth}%`}
          colorClass={getHealthColor(overallHealth)}
          bgClass={overallHealth >= 70 ? 'bg-green-50' : overallHealth >= 40 ? 'bg-yellow-50' : 'bg-red-50'}
        />
        <StatCard
          icon={FileText}
          label="Total Signals"
          value={signals.total || 0}
          subValue={`${signals.recent?.last7d || 0} this week`}
          colorClass="text-blue-600"
          bgClass="bg-blue-50"
        />
        <StatCard
          icon={DollarSign}
          label="Total Deals"
          value={deals.total || 0}
          subValue={`${deals.recent?.last6m || 0} in 6 months`}
          colorClass="text-green-600"
          bgClass="bg-green-50"
        />
        <StatCard
          icon={Users}
          label="Active Users"
          value={platform.activeUsers30d || 0}
          subValue={`of ${platform.users || 0} total`}
          colorClass="text-purple-600"
          bgClass="bg-purple-50"
        />
      </div>

      {/* Platform Counts */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <Building2 className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-gray-900">{platform.buyers || 0}</div>
          <div className="text-sm text-gray-500">Buyers</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <Building2 className="w-6 h-6 text-teal-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-gray-900">{platform.sellers || 0}</div>
          <div className="text-sm text-gray-500">Sellers</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <Zap className="w-6 h-6 text-amber-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-gray-900">{signals.uniqueBuyers || 0}</div>
          <div className="text-sm text-gray-500">Buyers with Signals</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Signal Data Coverage */}
        <Section
          title="Signal Data Coverage"
          icon={FileText}
          iconColor="text-blue-500"
          badge={`${signals.total || 0} signals`}
        >
          <p className="text-xs text-gray-500 my-3">
            How complete is the signal data?
          </p>
          <CoverageBar
            label="Therapeutic Area"
            count={signals.coverage?.therapeutic_area?.count || 0}
            total={signals.total}
            pct={signals.coverage?.therapeutic_area?.pct || 0}
          />
          <CoverageBar
            label="Modality"
            count={signals.coverage?.modality?.count || 0}
            total={signals.total}
            pct={signals.coverage?.modality?.pct || 0}
          />
          <CoverageBar
            label="Description"
            count={signals.coverage?.description?.count || 0}
            total={signals.total}
            pct={signals.coverage?.description?.pct || 0}
          />
          <CoverageBar
            label="Source URL"
            count={signals.coverage?.source_url?.count || 0}
            total={signals.total}
            pct={signals.coverage?.source_url?.pct || 0}
          />
        </Section>

        {/* Deal Data Coverage */}
        <Section
          title="Deal Data Coverage"
          icon={DollarSign}
          iconColor="text-green-500"
          badge={`${deals.total || 0} deals`}
        >
          <p className="text-xs text-gray-500 my-3">
            How complete is the deal data?
          </p>
          <CoverageBar
            label="Therapeutic Area"
            count={deals.coverage?.therapeutic_area?.count || 0}
            total={deals.total}
            pct={deals.coverage?.therapeutic_area?.pct || 0}
          />
          <CoverageBar
            label="Modality"
            count={deals.coverage?.modality?.count || 0}
            total={deals.total}
            pct={deals.coverage?.modality?.pct || 0}
          />
          <CoverageBar
            label="Upfront Payment"
            count={deals.coverage?.upfront_payment?.count || 0}
            total={deals.total}
            pct={deals.coverage?.upfront_payment?.pct || 0}
          />
          <CoverageBar
            label="Total Value"
            count={deals.coverage?.total_value?.count || 0}
            total={deals.total}
            pct={deals.coverage?.total_value?.pct || 0}
          />
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Signal Type Distribution */}
        <Section
          title="Signal Types Distribution"
          icon={Zap}
          iconColor="text-amber-500"
          badge={distributions.signalTypes?.length || 0}
        >
          <p className="text-xs text-gray-500 my-3">
            Distribution of signals by type
          </p>
          {distributions.signalTypes?.length > 0 ? (
            <DistributionBar items={distributions.signalTypes} maxCount={maxSignalTypeCount} />
          ) : (
            <p className="text-gray-400 text-sm py-4 text-center">No data</p>
          )}
        </Section>

        {/* Therapeutic Area Distribution */}
        <Section
          title="Therapeutic Areas (from Deals)"
          icon={Target}
          iconColor="text-purple-500"
          badge={distributions.therapeuticAreas?.length || 0}
        >
          <p className="text-xs text-gray-500 my-3">
            Distribution of deals by therapeutic area
          </p>
          {distributions.therapeuticAreas?.length > 0 ? (
            <DistributionBar items={distributions.therapeuticAreas} maxCount={maxTACount} />
          ) : (
            <p className="text-gray-400 text-sm py-4 text-center">No data</p>
          )}
        </Section>
      </div>

      {/* Action Items */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100 p-5">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-indigo-600" />
          Recommended Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {signalTACoverage < 50 && (
            <div className="flex items-start gap-2">
              <span className="text-red-500">1.</span>
              <span>Add therapeutic_area to signals via Signal Parser (currently {signalTACoverage}%)</span>
            </div>
          )}
          {signalModalityCoverage < 50 && (
            <div className="flex items-start gap-2">
              <span className="text-red-500">2.</span>
              <span>Add modality to signals via Signal Parser (currently {signalModalityCoverage}%)</span>
            </div>
          )}
          {dealModalityCoverage < 50 && (
            <div className="flex items-start gap-2">
              <span className="text-orange-500">3.</span>
              <span>Improve deal modality coverage (currently {dealModalityCoverage}%)</span>
            </div>
          )}
          {(signals.recent?.last7d || 0) === 0 && (
            <div className="flex items-start gap-2">
              <span className="text-yellow-500">4.</span>
              <span>No new signals this week - consider ingesting new data</span>
            </div>
          )}
          {signalTACoverage >= 50 && signalModalityCoverage >= 50 && dealModalityCoverage >= 50 && (
            <div className="flex items-start gap-2 text-green-600">
              <CheckCircle className="w-4 h-4 mt-0.5" />
              <span>Data quality is good! Keep monitoring.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
